import { apiClient } from "@/api/client";
import { AxiosError } from "axios";
import { create } from "zustand";

// ── New API types ─────────────────────────────────────────────────────────────

export interface RawPermission {
  id: string;
  name: string;
  /**
   * Friendly, client-facing label for the permission (e.g. "View campaigns").
   * Falls back to `name` when absent. Defined by the backend's curated
   * PermissionGroupingStaticService.
   */
  label?: string | null;
  desc: string | null;
  assigned: boolean;   // changed: was `checked`, now mirrors API field
}

export interface PermissionGroup {
  id: string;
  name: string;
  display_name: string;
  description: string | null;
  icon: string | null;
  sort_order: number;
  all_assigned: boolean;
  some_assigned: boolean;
  none_assigned: boolean;
  assigned_count: number;
  total_count: number;
  permissions: RawPermission[];
}

// Kept for backward-compat with the view layer (title → display_name alias)
export interface PermissionSection {
  group: PermissionGroup;
}

export interface SidebarSection {
  key: string;
  display_name: string;
  icon: string | null;
  sort_order: number;
  all_assigned: boolean;
  some_assigned: boolean;
  none_assigned: boolean;
  assigned_count: number;
  total_count: number;
  permission_groups: PermissionGroup[];
}

interface RolePermissionsResponse {
  role: { id: string; name: string };
  context: string | null;
  permission_groups: PermissionGroup[];
  sidebar?: SidebarSection[];
}

// ── Role types (unchanged) ────────────────────────────────────────────────────

interface Role {
  id: string;
  name: string;
  scope: "global" | "media";
  active: number;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

interface RoleResponse {
  success: boolean;
  message: string;
  data: Array<Role>;
}

// ── Store ─────────────────────────────────────────────────────────────────────

interface RoleState {
  roles: Array<Role> | null;
  selectedRole: Role | null;
  permissionGroups: Array<PermissionGroup> | null;   // replaces `permissions`
  sidebar: Array<SidebarSection> | null;
  sortField: string;
  setRole: (role: Role) => void;
  setRoleById: (id: string) => void;
  getPermissions: (roleId?: string, context?: string) => Promise<void>;
  getRoles: (sort?: string) => Promise<void>;
  savePermissions: (groups: Array<PermissionGroup>) => Promise<void>;
  setPermissions: (groups: Array<PermissionGroup> | null) => void;
}

const useRoleStore = create<RoleState>((set, get) => ({
  roles: [],
  selectedRole: null,
  permissionGroups: null,
  sidebar: null,
  sortField: '',

  setPermissions: (groups) => set({ permissionGroups: groups }),

  setRole: (role) => set({ selectedRole: role }),

  setRoleById: (id) => {
    const current = get().roles ?? [];
    if (current.length) {
      set({ selectedRole: current.find((r) => r.id === id) });
    }
  },

  getRoles: async (sort?: string) => {
    try {
      const params = sort ? { sort } : {};
      const { data } = await apiClient.get<RoleResponse>("/roles", { params });
      set({ roles: data.data, sortField: sort || '' });
    } catch (error) {
      throw new Error(extractMessage(error));
    }
  },

  /**
   * Fetches grouped permissions for a role from:
   *   GET /api/roles/{roleId}/permissions?context={context}
   *
   * The response already includes `assigned: true/false` on each permission,
   * so no second request or manual cross-referencing is needed.
   */
  getPermissions: async (roleId?: string, context?: string) => {
    try {
      const id = roleId ?? get().selectedRole?.id;
      if (!id) throw new Error("No role selected");

      const params = context ? { context } : {};
      const { data } = await apiClient.get<RolePermissionsResponse>(
        `/roles/${id}/permissions`,
        { params }
      );

      set({
        permissionGroups: data.permission_groups,
        sidebar: data.sidebar ?? null,
      });
    } catch (error) {
      throw new Error(extractMessage(error));
    }
  },

  savePermissions: async (groups) => {
    try {
      const id = get().selectedRole?.id;
      if (!id) throw new Error("No role selected");

      const permissions = groups.flatMap((g) =>
        g.permissions.filter((p) => p.assigned).map((p) => p.name)
      );

      await apiClient.post(`/roles/${id}/permissions`, { permissions });
    } catch (error) {
      throw new Error(extractMessage(error));
    }
  },
}));

function extractMessage(error: unknown): string {
  const axiosError = error as AxiosError<any>;
  return axiosError.response?.data?.message ?? "Something went wrong";
}

export default useRoleStore;